import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';
import { 
  XMarkIcon, 
  UserIcon, 
  CurrencyDollarIcon, 
  TruckIcon,
  DocumentTextIcon,
  ClockIcon,
  CalendarIcon
} from '@heroicons/react/24/outline';
import { payrollService } from '../../../services/accounting/payroll';
import { formatCurrency, formatDate } from '../../../utils/formatting';
import toast from 'react-hot-toast';
import PayslipModal from './PayslipModal';
import PaymentHistoryModal from './PaymentHistoryModal';

/**
 * EmployeeManagementModal Component
 * 
 * Modal for managing individual employee salary and commission settings
 */
const EmployeeManagementModal = ({ isOpen, onClose, onSuccess, employee }) => {
  const { t } = useTranslation();
  const { currentCompany, currentUser } = useAuth();
  
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('commissions'); // salary, commissions, payslips, history - Show commissions first
  const [showPayslipModal, setShowPayslipModal] = useState(false);
  const [showPaymentHistoryModal, setShowPaymentHistoryModal] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [payrollPeriods, setPayrollPeriods] = useState([]);
  const [employeeData, setEmployeeData] = useState({
    salary: 0,
    commissionRate: 0,
    commissionEligible: false,
    status: 'active'
  });
  const [commissions, setCommissions] = useState([]);
  const [selectedCommissions, setSelectedCommissions] = useState([]);
  const [dateFilter, setDateFilter] = useState('current_month');
  const [customDateRange, setCustomDateRange] = useState({
    startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });
  const [clientFilter, setClientFilter] = useState('all');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('all');
  const [availableClients, setAvailableClients] = useState([]);

  // Initialize employee data
  useEffect(() => {
    if (employee) {
      setEmployeeData({
        salary: employee.salary || 0,
        commissionRate: employee.commissionRate || 0,
        commissionEligible: employee.commissionEligible || false,
        status: employee.status || 'active'
      });
      loadCommissions();
      loadPayrollPeriods();
    }
  }, [employee]);

  // Load commissions when date filter changes
  useEffect(() => {
    if (employee && isOpen) {
      loadCommissions();
    }
  }, [dateFilter, customDateRange, employee, isOpen]);

  const loadCommissions = async () => {
    if (!employee || !currentCompany?.id) return;

    try {
      setLoading(true);
      
      // Calculate date range based on filter
      let startDate, endDate;
      const now = new Date();
      
      switch (dateFilter) {
        case 'current_month':
          startDate = new Date(now.getFullYear(), now.getMonth(), 1);
          endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
          break;
        case 'previous_month':
          startDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
          endDate = new Date(now.getFullYear(), now.getMonth(), 0);
          break;
        case 'custom':
          startDate = new Date(customDateRange.startDate);
          endDate = new Date(customDateRange.endDate);
          break;
        case 'all':
        default:
          startDate = new Date(2020, 0, 1); // Far back date
          endDate = new Date();
          break;
      }

      const commissionData = await payrollService.getEmployeeCommissions(
        employee.id,
        currentCompany.id,
        startDate,
        endDate
      );
      
      setCommissions(commissionData);
      
      // Extract unique clients from commissions
      const uniqueClients = commissionData.reduce((clients, commission) => {
        if (commission.clientId && commission.clientName && commission.clientName !== 'N/A') {
          const existingClient = clients.find(c => c.id === commission.clientId);
          if (!existingClient) {
            clients.push({
              id: commission.clientId,
              name: commission.clientName
            });
          }
        }
        return clients;
      }, []);
      
      setAvailableClients(uniqueClients.sort((a, b) => a.name.localeCompare(b.name)));
    } catch (error) {
      console.error('Error loading commissions:', error);
      toast.error(t('payroll.error.loading_commissions', 'Failed to load commissions'));
    } finally {
      setLoading(false);
    }
  };

  const loadPayrollPeriods = async () => {
    if (!currentCompany?.id) return;

    try {
      const periods = await payrollService.getPayrollPeriods(currentCompany.id);
      setPayrollPeriods(periods);
      
      // Auto-select the most recent period
      if (periods.length > 0 && !selectedPeriod) {
        setSelectedPeriod(periods[0]);
      }
    } catch (error) {
      console.error('Error loading payroll periods:', error);
      toast.error(t('payroll.error.loading_periods', 'Failed to load payroll periods'));
    }
  };

  const handleSalarySave = async () => {
    if (!employee?.id) return;

    try {
      setLoading(true);
      
      await payrollService.updateEmployeeSalary(
        employee.id,
        employeeData,
        currentUser?.uid || 'system'
      );
      
      toast.success(t('payroll.success.salary_updated', 'Employee salary updated successfully'));
      onSuccess();
      
    } catch (error) {
      console.error('Error updating salary:', error);
      toast.error(t('payroll.error.salary_update_failed', 'Failed to update employee salary'));
    } finally {
      setLoading(false);
    }
  };

  const handleCommissionPayment = async (commissionIds, markAsPaid = true) => {
    if (!commissionIds.length) return;

    try {
      setLoading(true);
      console.log('🔄 Processing commission payment:', { commissionIds, markAsPaid });
      
      await payrollService.updateCommissionPaymentStatus(
        commissionIds,
        markAsPaid,
        currentUser?.uid || 'system'
      );
      
      toast.success(
        markAsPaid 
          ? t('drivers.commissions_marked_paid', 'Commissions marked as paid')
          : t('drivers.commissions_marked_unpaid', 'Commissions marked as unpaid')
      );
      
      // Reload commissions
      await loadCommissions();
      setSelectedCommissions([]);
      
    } catch (error) {
      console.error('❌ Error updating commission payment status:', error);
      console.error('Error details:', error.message, error.stack);
      toast.error(t('drivers.commission_update_failed', 'Failed to update commission status'));
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = () => {
    if (selectedCommissions.length === commissions.length) {
      setSelectedCommissions([]);
    } else {
      setSelectedCommissions(commissions.map(c => c.id));
    }
  };

  const handleCommissionSelect = (commissionId) => {
    if (selectedCommissions.includes(commissionId)) {
      setSelectedCommissions(selectedCommissions.filter(id => id !== commissionId));
    } else {
      setSelectedCommissions([...selectedCommissions, commissionId]);
    }
  };

  const getDateFilterLabel = () => {
    switch (dateFilter) {
      case 'current_month':
        return t('common.this_month', t('common.this_month'));
      case 'previous_month':
        return t('common.last_month', 'Last Month');
      case 'custom':
        return t('common.custom_range', t('common.custom_range'));
      case 'all':
        return t('common.all_time', 'All Time');
      default:
        return '';
    }
  };

  const totalCommissions = commissions.reduce((sum, c) => sum + (c.amount || 0), 0);
  const paidCommissions = commissions.filter(c => c.isPaid).reduce((sum, c) => sum + (c.amount || 0), 0);
  const unpaidCommissions = totalCommissions - paidCommissions;

  if (!isOpen || !employee) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {t('payroll.manage_employee', 'Manage Employee')} - {employee.name}
            </h2>
            <p className="text-sm text-gray-500">
              {employee.role} • {employee.type === 'driver' ? t('payroll.driver', 'Driver') : t('payroll.employee', 'Employee')}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={loading}
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
            {[
              { id: 'commissions', name: t('payroll.commissions', 'Commissions'), icon: TruckIcon },
              { id: 'salary', name: t('payroll.salary_settings', 'Salary Settings'), icon: UserIcon },
              { id: 'payslips', name: t('payroll.payslips', 'Payslips'), icon: DocumentTextIcon },
              { id: 'history', name: t('payroll.payment_history', 'Payment History'), icon: ClockIcon }
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    group inline-flex items-center border-b-2 py-4 px-1 text-sm font-medium
                    ${activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
                    }
                  `}
                >
                  <Icon
                    className={`
                      -ml-0.5 mr-2 h-5 w-5
                      ${activeTab === tab.id
                        ? 'text-blue-500'
                        : 'text-gray-400 group-hover:text-gray-500'
                      }
                    `}
                    aria-hidden="true"
                  />
                  {tab.name}
                </button>
              );
            })}
          </nav>
        </div>

        <div className="overflow-y-auto max-h-[calc(90vh-200px)]">
          {/* Salary Settings Tab */}
          {activeTab === 'salary' && (
            <div className="p-6 space-y-6">
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                  <UserIcon className="w-5 h-5 mr-2 text-blue-600" />
                  {t('payroll.employee_info', 'Employee Information')}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <label className="block text-gray-500">{t('payroll.employee_name', 'Employee Name')}</label>
                    <div className="font-medium">{employee.name}</div>
                  </div>
                  <div>
                    <label className="block text-gray-500">{t('payroll.employee_id', 'Employee ID')}</label>
                    <div className="font-medium">{employee.employeeId || employee.id}</div>
                  </div>
                  <div>
                    <label className="block text-gray-500">{t('payroll.role', 'Role')}</label>
                    <div className="font-medium">{employee.role}</div>
                  </div>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                  <CurrencyDollarIcon className="w-5 h-5 mr-2 text-green-600" />
                  {t('payroll.salary_settings', 'Salary Settings')}
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('payroll.monthly_salary', 'Monthly Salary')}
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={employeeData.salary || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        setEmployeeData({ 
                          ...employeeData, 
                          salary: value === '' ? 0 : parseFloat(value) || 0 
                        });
                      }}
                      onFocus={(e) => {
                        // Clear the field when focused if it's 0
                        if (e.target.value === '0') {
                          e.target.value = '';
                        }
                      }}
                      className="form-input"
                      placeholder="Enter salary amount"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('payroll.employee_status', 'Employee Status')}
                    </label>
                    <select
                      value={employeeData.status}
                      onChange={(e) => setEmployeeData({ ...employeeData, status: e.target.value })}
                      className="form-select"
                    >
                      <option value="active">{t('common.active', t('common.active'))}</option>
                      <option value="inactive">{t('common.inactive', t('common.inactive'))}</option>
                    </select>
                  </div>

                  {employee.type === 'driver' && (
                    <>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="commissionEligible"
                          checked={employeeData.commissionEligible}
                          onChange={(e) => setEmployeeData({ ...employeeData, commissionEligible: e.target.checked })}
                          className="form-checkbox"
                        />
                        <label htmlFor="commissionEligible" className="ml-2 text-sm text-gray-700">
                          {t('payroll.commission_eligible', 'Commission Eligible')}
                        </label>
                      </div>

                      {employeeData.commissionEligible && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            {t('payroll.commission_rate', 'Commission Rate')} (%)
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            max="100"
                            value={employeeData.commissionRate || ''}
                            onChange={(e) => {
                              const value = e.target.value;
                              setEmployeeData({ 
                                ...employeeData, 
                                commissionRate: value === '' ? 0 : parseFloat(value) || 0 
                              });
                            }}
                            onFocus={(e) => {
                              // Clear the field when focused if it's 0
                              if (e.target.value === '0') {
                                e.target.value = '';
                              }
                            }}
                            className="form-input"
                            placeholder="Enter commission rate %"
                          />
                        </div>
                      )}
                    </>
                  )}
                </div>

                <div className="mt-6 flex justify-end">
                  <button
                    onClick={handleSalarySave}
                    className="btn btn-primary"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        {t('common.saving', 'Saving...')}
                      </>
                    ) : (
                      <>
                        <CurrencyDollarIcon className="w-4 h-4 mr-2" />
                        {t('payroll.save_salary', 'Save Salary Settings')}
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Commissions Tab */}
          {activeTab === 'commissions' && (
            <div className="p-6 space-y-6">
              {/* Commission Summary */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-blue-600">{formatCurrency(totalCommissions)}</div>
                  <div className="text-sm text-blue-700">{t('payroll.total_commissions', 'Total Commissions')}</div>
                </div>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-green-600">{formatCurrency(paidCommissions)}</div>
                  <div className="text-sm text-green-700">{t('payroll.paid_commissions', 'Paid Commissions')}</div>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-center">
                  <div className="text-2xl font-bold text-amber-600">{formatCurrency(unpaidCommissions)}</div>
                  <div className="text-sm text-amber-700">{t('payroll.unpaid_commissions', 'Unpaid Commissions')}</div>
                </div>
              </div>

              {/* Date Filter */}
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">
                      {t('payroll.commission_history', 'Commission History')} - {getDateFilterLabel()}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {t('payroll.showing_commissions', 'Showing {{count}} commissions', { count: commissions.length })}
                    </p>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-2">
                    <select
                      value={dateFilter}
                      onChange={(e) => setDateFilter(e.target.value)}
                      className="form-select"
                    >
                      <option value="current_month">{t('common.this_month', t('common.this_month'))}</option>
                      <option value="previous_month">{t('common.last_month', 'Last Month')}</option>
                      <option value="custom">{t('common.custom_range', t('common.custom_range'))}</option>
                      <option value="all">{t('common.all_time', 'All Time')}</option>
                    </select>
                    
                    <select
                      value={clientFilter}
                      onChange={(e) => setClientFilter(e.target.value)}
                      className="form-select"
                    >
                      <option value="all">{t('common.all_clients', 'All Clients')}</option>
                      {availableClients.map(client => (
                        <option key={client.id} value={client.id}>
                          {client.companyName || client.contactPerson || client.name}
                        </option>
                      ))}
                    </select>

                    <select
                      value={paymentStatusFilter}
                      onChange={(e) => setPaymentStatusFilter(e.target.value)}
                      className="form-select"
                    >
                      <option value="all">{t('common.all_statuses', 'All Statuses')}</option>
                      <option value="paid">{t('common.paid', t('common.paid'))}</option>
                      <option value="unpaid">{t('common.unpaid', t('common.unpaid'))}</option>
                    </select>
                    
                    {selectedCommissions.length > 0 && (
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleCommissionPayment(selectedCommissions, true)}
                          className="btn btn-sm btn-success"
                          disabled={loading}
                        >
                          {t('payroll.mark_paid', 'Mark Paid')}
                        </button>
                        <button
                          onClick={() => handleCommissionPayment(selectedCommissions, false)}
                          className="btn btn-sm btn-warning"
                          disabled={loading}
                        >
                          {t('payroll.mark_unpaid', 'Mark Unpaid')}
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Custom Date Range */}
                {dateFilter === 'custom' && (
                  <div className="mt-4 grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('common.from_date', 'From Date')}
                      </label>
                      <input
                        type="date"
                        value={customDateRange.startDate}
                        onChange={(e) => setCustomDateRange({ ...customDateRange, startDate: e.target.value })}
                        className="form-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('common.to_date', 'To Date')}
                      </label>
                      <input
                        type="date"
                        value={customDateRange.endDate}
                        onChange={(e) => setCustomDateRange({ ...customDateRange, endDate: e.target.value })}
                        className="form-input"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Commissions Table */}
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>
                          <input
                            type="checkbox"
                            checked={selectedCommissions.length === commissions.length && commissions.length > 0}
                            onChange={handleSelectAll}
                          />
                        </th>
                        <th>{t('shipments.shipment_number', 'Shipment #')}</th>
                        <th>{t('common.client', 'Client')}</th>
                        <th>{t('shipments.route', 'Route')}</th>
                        <th>{t('shipments.delivery_date', 'Delivery Date')}</th>
                        <th>{t('payroll.commission_amount', 'Commission Amount')}</th>
                        <th>{t('common.payment_status', 'Payment Status')}</th>
                        <th>{t('common.actions', t('common.actions'))}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {commissions
                        .filter(commission => {
                          // Client filter
                          if (clientFilter !== 'all' && commission.clientId !== clientFilter) {
                            return false;
                          }
                          
                          // Payment status filter
                          if (paymentStatusFilter !== 'all') {
                            if (paymentStatusFilter === 'paid' && !commission.isPaid) {
                              return false;
                            }
                            if (paymentStatusFilter === 'unpaid' && commission.isPaid) {
                              return false;
                            }
                          }
                          
                          return true;
                        })
                        .map(commission => (
                        <tr key={commission.id}>
                          <td>
                            <input
                              type="checkbox"
                              checked={selectedCommissions.includes(commission.id)}
                              onChange={() => handleCommissionSelect(commission.id)}
                            />
                          </td>
                          <td>
                            <a
                              href={`/shipments/${commission.shipmentId}`}
                              className="text-blue-600 hover:text-blue-800 font-medium underline cursor-pointer"
                              onClick={(e) => {
                                e.preventDefault();
                                // Navigate to shipment details
                                window.location.href = `/shipments/${commission.shipmentId}`;
                              }}
                            >
                              {commission.trackingNumber || `SH${commission.shipmentId.slice(-6)}`}
                            </a>
                          </td>
                          <td className="font-medium">{commission.clientName}</td>
                          <td>{commission.origin} → {commission.destination}</td>
                          <td>{formatDate(commission.deliveryDate)}</td>
                          <td className="font-medium text-green-600">{formatCurrency(commission.amount)}</td>
                          <td>
                            <span className={`badge ${
                              commission.isPaid 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-amber-100 text-amber-800'
                            }`}>
                              {commission.isPaid 
                                ? t('common.paid', t('common.paid'))
                                : t('common.unpaid', t('common.unpaid'))
                              }
                            </span>
                          </td>
                          <td>
                            <button
                              onClick={() => handleCommissionPayment([commission.id], !commission.isPaid)}
                              className={`btn btn-sm ${
                                commission.isPaid ? 'btn-warning' : 'btn-success'
                              }`}
                              disabled={loading}
                            >
                              {commission.isPaid 
                                ? t('payroll.mark_unpaid', 'Mark Unpaid')
                                : t('payroll.mark_paid', 'Mark Paid')
                              }
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {commissions.length === 0 && (
                  <div className="text-center py-12">
                    <TruckIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      {t('payroll.no_commissions', 'No Commissions Found')}
                    </h3>
                    <p className="text-gray-500">
                      {t('payroll.no_commissions_period', 'No commissions found for the selected period.')}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Payslips Tab */}
          {activeTab === 'payslips' && (
            <div className="p-6 space-y-6">
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900 flex items-center">
                    <DocumentTextIcon className="w-5 h-5 mr-2 text-blue-600" />
                    {t('payroll.employee_payslips', 'Employee Payslips')}
                  </h3>
                  <div className="flex items-center space-x-2">
                    <select
                      value={selectedPeriod?.id || ''}
                      onChange={(e) => {
                        const period = payrollPeriods.find(p => p.id === e.target.value);
                        setSelectedPeriod(period);
                      }}
                      className="form-select"
                    >
                      <option value="">{t('payroll.select_period', 'Select Period')}</option>
                      {payrollPeriods.map(period => (
                        <option key={period.id} value={period.id}>
                          {period.year}-{String(period.month).padStart(2, '0')} ({t(`payroll.status.${period.status}`, period.status)})
                        </option>
                      ))}
                    </select>
                    {selectedPeriod && (
                      <button
                        onClick={() => setShowPayslipModal(true)}
                        className="btn btn-primary btn-sm"
                      >
                        <DocumentTextIcon className="w-4 h-4 mr-2" />
                        {t('payroll.view_payslip', 'View Payslip')}
                      </button>
                    )}
                  </div>
                </div>

                {payrollPeriods.length === 0 ? (
                  <div className="text-center py-12">
                    <CalendarIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      {t('payroll.no_periods', 'No Payroll Periods')}
                    </h3>
                    <p className="text-gray-500">
                      {t('payroll.no_periods_desc', 'No payroll periods have been created yet.')}
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {payrollPeriods.map(period => (
                      <div
                        key={period.id}
                        className={`border rounded-lg p-4 cursor-pointer transition-colors ${
                          selectedPeriod?.id === period.id
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                        onClick={() => setSelectedPeriod(period)}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="font-medium text-gray-900">
                            {period.year}-{String(period.month).padStart(2, '0')}
                          </h4>
                          <span className={`badge ${
                            period.status === 'paid' ? 'bg-green-100 text-green-800' :
                            period.status === 'accrued' ? 'bg-blue-100 text-blue-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {t(`payroll.status.${period.status}`, period.status)}
                          </span>
                        </div>
                        <div className="text-sm text-gray-600">
                          <div>{t('payroll.total_payable', 'Total Payable')}: {formatCurrency(period.totalPayable || 0)}</div>
                          <div>{t('payroll.employees', 'Employees')}: {period.employeeCount || 0}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Payment History Tab */}
          {activeTab === 'history' && (
            <div className="p-6 space-y-6">
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900 flex items-center">
                    <ClockIcon className="w-5 h-5 mr-2 text-blue-600" />
                    {t('payroll.payment_history', 'Payment History')}
                  </h3>
                  <button
                    onClick={() => setShowPaymentHistoryModal(true)}
                    className="btn btn-primary btn-sm"
                  >
                    <ClockIcon className="w-4 h-4 mr-2" />
                    {t('payroll.view_full_history', 'View Full History')}
                  </button>
                </div>

                <div className="text-center py-12">
                  <ClockIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    {t('payroll.payment_history_preview', 'Payment History Preview')}
                  </h3>
                  <p className="text-gray-500 mb-4">
                    {t('payroll.payment_history_desc', 'Click "View Full History" to see detailed payment records including payroll and commission payments.')}
                  </p>
                  <button
                    onClick={() => setShowPaymentHistoryModal(true)}
                    className="btn btn-primary"
                  >
                    <ClockIcon className="w-4 h-4 mr-2" />
                    {t('payroll.view_full_history', 'View Full History')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="btn btn-secondary"
            disabled={loading}
          >
            {t('common.close', t('common.close'))}
          </button>
        </div>
      </div>

      {/* Payslip Modal */}
      <PayslipModal
        isOpen={showPayslipModal}
        onClose={() => setShowPayslipModal(false)}
        employee={employee}
        period={selectedPeriod}
      />

      {/* Payment History Modal */}
      <PaymentHistoryModal
        isOpen={showPaymentHistoryModal}
        onClose={() => setShowPaymentHistoryModal(false)}
        employee={employee}
      />
    </div>
  );
};

export default EmployeeManagementModal;